/**
 * Group Expansions section for the Test Suite Configuration page.
 *
 * Displays group expansion entries grouped by parameter name. Each entry
 * is an editable row with label, expands_to (as a TokenGroup with text
 * input), and weight. Users can add new entries per parameter group and
 * remove existing entries. Validation errors are shown for entries with
 * empty expands_to arrays.
 *
 * @see Requirements 9.1–9.4
 */

import React, { useState } from 'react';
import {
  ExpandableSection,
  SpaceBetween,
  FormField,
  Input,
  Button,
  Header,
  Container,
  ColumnLayout,
  Box,
  TokenGroup,
  Toggle,
} from '@amzn/awsui-components-react';
import { ConfigPageAction, ConfigPageState, ValidationErrors } from './types';

// ─── Constants ─────────────────────────────────────────────────────────

const DEFAULT_WEIGHT = 1.0;

// ─── Props ─────────────────────────────────────────────────────────────

export interface GroupExpansionsSectionProps {
  state: ConfigPageState;
  dispatch: React.Dispatch<ConfigPageAction>;
  validationErrors: ValidationErrors;
}

// ─── Sub-component: ExpandsTo Input with TokenGroup ────────────────────

interface ExpandsToInputProps {
  paramName: string;
  index: number;
  values: string[];
  onUpdate: (updated: string[]) => void;
  errorText?: string;
  disabled?: boolean;
}

const ExpandsToInput: React.FC<ExpandsToInputProps> = ({
  paramName,
  index,
  values,
  onUpdate,
  errorText,
  disabled,
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (key: string) => {
    if (key === 'Enter' && inputValue.trim()) {
      const trimmed = inputValue.trim();
      if (!values.includes(trimmed)) {
        onUpdate([...values, trimmed]);
      }
      setInputValue('');
    }
  };

  const handleDismiss = (itemIndex: number) => {
    onUpdate(values.filter((_, i) => i !== itemIndex));
  };

  return (
    <FormField
      label="Expands to"
      description="Values this group label expands to"
      errorText={errorText}
    >
      <SpaceBetween size="xs">
        <Input
          value={inputValue}
          onChange={({ detail }) => setInputValue(detail.value)}
          onKeyDown={({ detail }) => handleKeyDown(detail.key)}
          placeholder="Type a value and press Enter"
          ariaLabel={`Add expansion value for ${paramName} entry ${index + 1}`}
          disabled={disabled}
        />
        {values.length > 0 && (
          <TokenGroup
            items={values.map((val) => ({
              label: val,
              dismissLabel: disabled ? undefined : `Remove ${val}`,
            }))}
            onDismiss={disabled ? undefined : ({ detail }) => handleDismiss(detail.itemIndex)}
          />
        )}
      </SpaceBetween>
    </FormField>
  );
};

// ─── Component ─────────────────────────────────────────────────────────

const GroupExpansionsSection: React.FC<GroupExpansionsSectionProps> = ({
  state,
  dispatch,
  validationErrors,
}) => {
  const groupEntries = Object.entries(state.groupExpansion);

  const handleFieldChange = (path: string, value: unknown) => {
    dispatch({ type: 'UPDATE_FIELD', path, value });
  };

  const handleNumberChange = (path: string, rawValue: string) => {
    const parsed = parseFloat(rawValue);
    handleFieldChange(path, isNaN(parsed) ? rawValue : parsed);
  };

  const handleLabelChange = (paramName: string, index: number, value: string) => {
    dispatch({
      type: 'UPDATE_GROUP_EXPANSION',
      paramName,
      index,
      field: 'label',
      value,
    });
  };

  const handleExpandsToChange = (paramName: string, index: number, value: string[]) => {
    dispatch({
      type: 'UPDATE_GROUP_EXPANSION',
      paramName,
      index,
      field: 'expands_to',
      value,
    });
  };

  const handleWeightChange = (paramName: string, index: number, rawValue: string) => {
    const parsed = parseFloat(rawValue);
    dispatch({
      type: 'UPDATE_GROUP_EXPANSION',
      paramName,
      index,
      field: 'weight',
      value: isNaN(parsed) ? rawValue : parsed,
    });
  };

  const handleAddEntry = (paramName: string) => {
    dispatch({
      type: 'ADD_GROUP_EXPANSION',
      paramName,
      entry: { label: '', expands_to: [], weight: DEFAULT_WEIGHT },
    });
  };

  const handleRemoveEntry = (paramName: string, index: number) => {
    dispatch({
      type: 'REMOVE_GROUP_EXPANSION',
      paramName,
      index,
    });
  };

  return (
    <ExpandableSection
      headerText="Group Expansions"
      variant="container"
      defaultExpanded
    >
      <SpaceBetween size="l">
        {/* ── Shared settings — rendered once ──────────────────────── */}
        <Container header={<Header variant="h3">Group Expansion Settings</Header>}>
          <SpaceBetween size="s">
            <FormField
              label="Enabled"
              description="Enable group label expansion in generated questions"
            >
              <Toggle
                checked={state.groupExpansionEnabled}
                onChange={({ detail }) =>
                  handleFieldChange('groupExpansionEnabled', detail.checked)
                }
                ariaLabel="Enable group expansion"
              >
                {state.groupExpansionEnabled ? 'Enabled' : 'Disabled'}
              </Toggle>
            </FormField>

            <FormField
              label="Probability"
              description="Probability of using group labels in a generated question"
              errorText={validationErrors['groupExpansionProbability']}
              constraintText="Must be between 0.0 and 1.0"
            >
              <Input
                value={String(state.groupExpansionProbability)}
                onChange={({ detail }) =>
                  handleNumberChange('groupExpansionProbability', detail.value)
                }
                type="number"
                inputMode="decimal"
                ariaLabel="Group expansion probability"
              />
            </FormField>
          </SpaceBetween>
        </Container>

        {groupEntries.length === 0 ? (
          <Box color="text-status-inactive" padding={{ vertical: 's' }}>
            No group expansions configured. Group expansions will appear after
            pre-population.
          </Box>
        ) : (
          <div style={{ opacity: state.groupExpansionEnabled ? 1 : 0.5 }}>
            <SpaceBetween size="l">
        {groupEntries.map(([paramName, entries]) => (
          <Container
            key={paramName}
            header={<Header variant="h3">{paramName}</Header>}
          >
            <SpaceBetween size="m">
              {entries.map((entry, index) => (
                <SpaceBetween key={entry._stableKey ?? `${paramName}-${index}`} size="s">
                  <ColumnLayout columns={3}>
                    <FormField
                      label="Label"
                      description="Group label name"
                    >
                      <Input
                        value={entry.label}
                        onChange={({ detail }) =>
                          handleLabelChange(paramName, index, detail.value)
                        }
                        placeholder="e.g. North America"
                        ariaLabel={`${paramName} entry ${index + 1} label`}
                        disabled={!state.groupExpansionEnabled}
                      />
                    </FormField>

                    <ExpandsToInput
                      paramName={paramName}
                      index={index}
                      values={entry.expands_to}
                      onUpdate={(updated) =>
                        handleExpandsToChange(paramName, index, updated)
                      }
                      errorText={
                        validationErrors[
                          `groupExpansion.${paramName}[${index}].expands_to`
                        ]
                      }
                      disabled={!state.groupExpansionEnabled}
                    />

                    <FormField
                      label="Weight"
                      description="Sampling weight (0.0–1.0)"
                    >
                      <SpaceBetween size="xs" direction="horizontal">
                        <Input
                          value={String(entry.weight)}
                          onChange={({ detail }) =>
                            handleWeightChange(paramName, index, detail.value)
                          }
                          type="number"
                          inputMode="decimal"
                          ariaLabel={`${paramName} entry ${index + 1} weight`}
                          disabled={!state.groupExpansionEnabled}
                        />
                        <Button
                          iconName="remove"
                          variant="icon"
                          onClick={() => handleRemoveEntry(paramName, index)}
                          ariaLabel={`Remove ${paramName} entry ${index + 1}`}
                          disabled={!state.groupExpansionEnabled}
                        />
                      </SpaceBetween>
                    </FormField>
                  </ColumnLayout>
                </SpaceBetween>
              ))}

              <Button
                iconName="add-plus"
                onClick={() => handleAddEntry(paramName)}
                disabled={!state.groupExpansionEnabled}
              >
                Add Entry
              </Button>
            </SpaceBetween>
          </Container>
        ))}
            </SpaceBetween>
          </div>
        )}
      </SpaceBetween>
    </ExpandableSection>
  );
};

export default GroupExpansionsSection;
